/**
 * ReachabilityCard — Phase-1 owner-only diagnostic.
 *
 * Surfaces two rates from `/api/admin/reachability-summary`:
 *   • Network Reachability Rate — % of attempts that got any server response
 *   • Usable Response Rate      — % of attempts that returned 2xx
 *
 * A 403 or WAF challenge counts toward Reachability (server responded) but
 * NOT toward Usable Response (couldn't complete the check). This separation
 * lets us tell "our cluster IP is blocked" from "the site is broken" at a
 * glance. Internal-only — no customer-facing surface.
 */
import { useEffect, useState } from "react";
import { Globe } from "lucide-react";
import api from "../../lib/api";

const pct = (n) => (typeof n === "number" ? `${(n * 100).toFixed(1)}%` : "—");

function toneFor(rate) {
  if (rate == null) return "muted";
  if (rate >= 0.99) return "green";
  if (rate >= 0.90) return "amber";
  return "red";
}

const TONE = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red:   "text-red-700",
  muted: "text-charcoal-500",
};

export default function ReachabilityCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/admin/reachability-summary?days=7")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setErr(e); });
    return () => { mounted = false; };
  }, []);

  if (err) return null; // silent on error — owner-only diagnostic
  const netRate = data?.network_reachability_rate;
  const useRate = data?.usable_response_rate;
  const outcomes = data?.outcome_breakdown || {};
  const wafBreakdown = data?.waf_breakdown || {};
  const top = data?.top_failing_hosts || [];

  return (
    <div
      className="rounded-2xl bg-white ring-1 ring-black/5 p-5"
      data-testid="reachability-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-charcoal-400" strokeWidth={1.75} />
        <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-500">
          Reachability · Last 7 days · Internal
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">
            Network Reachability
          </p>
          <p className={`mt-1 text-3xl font-semibold tabular-nums ${TONE[toneFor(netRate)]}`}
             data-testid="reachability-net-rate">
            {pct(netRate)}
          </p>
          <p className="text-[11px] text-charcoal-500 mt-0.5">Any server response received</p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400">
            Usable Response
          </p>
          <p className={`mt-1 text-3xl font-semibold tabular-nums ${TONE[toneFor(useRate)]}`}
             data-testid="reachability-usable-rate">
            {pct(useRate)}
          </p>
          <p className="text-[11px] text-charcoal-500 mt-0.5">Returned a 2xx status</p>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-charcoal-500 flex flex-wrap gap-x-4 gap-y-1">
        <span>Total attempts: <span className="font-mono text-charcoal-800">{data?.total_attempts ?? "—"}</span></span>
        <span>Hosts &lt; 90% usable: <span className="font-mono text-charcoal-800">{data?.hosts_below_90_usable ?? "—"}</span></span>
      </div>

      {Object.keys(outcomes).length > 0 && (
        <div className="mt-4 pt-3 border-t border-black/5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-2">
            Outcome breakdown
          </p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(outcomes).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <span key={k}
                    className="inline-block px-2 py-0.5 rounded-full bg-charcoal-50 border border-black/5 text-[11px] font-mono text-charcoal-700"
                    data-testid={`reachability-outcome-${k}`}>
                {k} · {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {Object.keys(data?.tier_breakdown || {}).length > 0 && (
        <div className="mt-4 pt-3 border-t border-black/5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-2">
            Sophistication tier · what's pulling weight
          </p>
          <div className="space-y-1.5">
            {Object.entries(data.tier_breakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([tier, count]) => {
                const succ = (data.tier_success_breakdown || {})[tier] || 0;
                const rate = count > 0 ? succ / count : 0;
                return (
                  <div key={tier}
                       className="flex items-center justify-between gap-3 text-[12px]"
                       data-testid={`reachability-tier-${tier}`}>
                    <span className="font-mono text-charcoal-800 truncate">{tier}</span>
                    <span className={`font-mono ${TONE[toneFor(rate)]}`}>
                      {pct(rate)} success · {count} attempts
                    </span>
                  </div>
                );
              })}
          </div>
          <p className="mt-2 text-[10.5px] text-charcoal-400 leading-relaxed">
            t1_* = curl_cffi baseline · t2_* = alt-fingerprint retry · t3_playwright = browser fallback.
            Rising t3 share signals more hosts need heavy escalation.
          </p>
        </div>
      )}

      {Object.keys(wafBreakdown).length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-1.5">
            WAF fingerprints
          </p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(wafBreakdown).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <span key={k}
                    className="inline-block px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-[11px] font-mono text-amber-800"
                    data-testid={`reachability-waf-${k}`}>
                {k} · {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {top.length > 0 && (
        <div className="mt-4 pt-3 border-t border-black/5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal-400 mb-2">
            Top failing hosts
          </p>
          <div className="space-y-1.5">
            {top.slice(0, 5).map((h) => (
              <div key={h.host}
                   className="flex items-center justify-between gap-3 text-[12px]"
                   data-testid={`reachability-failing-${h.host}`}>
                <span className="font-mono text-charcoal-800 truncate">{h.host}</span>
                <span className={`font-mono ${TONE[toneFor(h.usable_rate)]}`}>
                  {pct(h.usable_rate)} usable · {h.attempts} attempts
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
